import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import * as S from './styled'

export type TeeColor = {
  name: string
  color: string
}

// The colours a course sets its tees out in, shared with the places that
// fill in a whole set of tees at once.
export const TEE_COLORS: TeeColor[] = [
  { name: 'Svart', color: '#000000' },
  { name: 'Vit', color: '#ffffff' },
  { name: 'Gul', color: '#ffff00' },
  { name: 'Blå', color: '#0000ff' },
  { name: 'Röd', color: '#ff0000' },
  { name: 'Orange', color: '#ffa500' },
]

// Its own group, so it always sits alone on the last row
const NO_COLOR: TeeColor = { name: 'None', color: '#ffffff' }

const POPOVER_WIDTH = 352
const VIEWPORT_MARGIN = 8
const ANCHOR_GAP = 4

type ColorInputProps = {
  value?: string
  onChange: (color: string) => void
  label?: string
}

const ColorInput = ({
  value = '#ffffff',
  onChange,
  label,
}: ColorInputProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedColor, setSelectedColor] = useState(value)
  const [position, setPosition] = useState<{
    top: number
    left: number
  } | null>(null)

  const pickerRef = useRef<HTMLDivElement | null>(null)
  const modalRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    setSelectedColor(value)
  }, [value])

  const handleColorSelect = (color: string) => {
    setSelectedColor(color)
    onChange(color)
    setIsOpen(false)
  }

  const handleClickOutside = (e) => {
    if (
      pickerRef.current &&
      !pickerRef.current.contains(e.target) &&
      modalRef.current &&
      !modalRef.current.contains(e.target)
    ) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Keep the popover inside the viewport: clamp horizontally, and flip it above
  // the button when there is no room below.
  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null)
      return
    }

    const updatePosition = () => {
      const anchor = pickerRef.current?.getBoundingClientRect()
      if (!anchor) return

      const height = modalRef.current?.offsetHeight ?? 0
      const maxLeft = window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN
      const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.left, maxLeft))

      const spaceBelow = window.innerHeight - anchor.bottom - VIEWPORT_MARGIN
      const spaceAbove = anchor.top - VIEWPORT_MARGIN
      const flipAbove = spaceBelow < height && spaceAbove > spaceBelow
      const top = flipAbove
        ? anchor.top - height - ANCHOR_GAP
        : anchor.bottom + ANCHOR_GAP

      setPosition({
        top: Math.max(
          VIEWPORT_MARGIN,
          Math.min(top, window.innerHeight - height - VIEWPORT_MARGIN)
        ),
        left,
      })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen])

  const renderOption = ({ name, color }: TeeColor) => (
    <S.TeeCard
      key={name}
      $selected={color === selectedColor}
      onClick={() => handleColorSelect(color)}
      style={{ cursor: 'pointer' }}
    >
      <S.TeeCardColor>
        <S.TeeCardSwatch $color={color} title={name} aria-label={name} />
      </S.TeeCardColor>
      <S.TeeCardName>{name}</S.TeeCardName>
    </S.TeeCard>
  )

  return (
    <div
      style={{ position: 'relative', display: 'inline-block' }}
      ref={pickerRef}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label={label}
        style={{
          width: '32px',
          height: '32px',
          backgroundColor: selectedColor,
          border: '2px solid #ccc',
          borderRadius: '50%',
          cursor: 'pointer',
          boxShadow: `0 0 0 2px white, 0 0 0 4px ${selectedColor}`,
        }}
      />
      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={modalRef}
            style={{
              position: 'fixed',
              top: `${position?.top ?? 0}px`,
              left: `${position?.left ?? 0}px`,
              visibility: position ? 'visible' : 'hidden',
              backgroundColor: '#fff',
              padding: '16px',
              border: '1px solid #ccc',
              borderRadius: '12px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
              zIndex: 10000,
              width: `${POPOVER_WIDTH}px`,
              boxSizing: 'border-box',
            }}
          >
            <div style={{ fontWeight: '600', marginBottom: '12px' }}>
              Set color
            </div>

            <S.OptionGroupHeading>Standard colors</S.OptionGroupHeading>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
              }}
            >
              {TEE_COLORS.map(renderOption)}
            </div>

            <S.OptionGroupHeading style={{ marginTop: '16px' }}>
              Hectometer tee
            </S.OptionGroupHeading>
            <div style={{ display: 'flex' }}>{renderOption(NO_COLOR)}</div>
          </div>,
          document.body
        )}
    </div>
  )
}

export default ColorInput
